import { defineStore } from 'pinia'
import { Card } from '~/static/cards'
import { CardType } from '~/static/enums/card-type'
import { EditorData } from '~/static/enums/editor-state'

interface Cards {
  cards: Card[]
  totalCount?: number
  page: number
}

interface Filter {
  directory_id: number | null
  search_string: string | null
}

interface CardList {
  [CardType.ProductCard]: Cards
  [CardType.Template]: Cards
}

interface CardListStore {
  filters: Filter
  isLoading: {
    [CardType.ProductCard]: boolean
    [CardType.Template]: boolean
  }
  list: CardList
}

export const useCardList = defineStore('card-list', {
  state: (): CardListStore => {
    return {
      filters: {
        directory_id: null,
        search_string: null,
      },
      isLoading: {
        [CardType.ProductCard]: false,
        [CardType.Template]: false,
      },
      list: {
        [CardType.ProductCard]: {
          cards: [],
          totalCount: undefined,
          page: 1,
        },
        [CardType.Template]: {
          cards: [],
          totalCount: undefined,
          page: 1,
        },
      },
    }
  },
  actions: {
    async setFilter(filters: Partial<Filter>) {
      this.list[CardType.ProductCard].cards = []
      this.list[CardType.ProductCard].page = 1
      this.list[CardType.ProductCard].totalCount = undefined
      Object.keys(filters).forEach((key) => {
        const filterKey: keyof Filter = <keyof Filter>key
        this.filters[filterKey] = <null>filters[filterKey]
      })
      await this.loadNextStep(CardType.ProductCard)
    },
    async loadNextStep(type: CardType) {
      if (this.isLoading[type] || (
        this.list[type].totalCount !== undefined &&
        this.list[type].cards.length >= (this.list[type].totalCount ?? -1)
      )) {
        return
      }

      if (type === CardType.Template) {
        this.filters = {
          directory_id: null,
          search_string: null,
        }
      }

      this.isLoading[type] = true

      interface CardsResponse {
        cards: Card[]
        total_count: number
      }

      const page = this.list[type].page
      const calculateLimit = (): number => {
        if (type === CardType.ProductCard) {
          return page === 1 ? 23 : 24
        } else {
          return 24
        }
      }

      const calculateOffset = (): number => {
        if (type === CardType.ProductCard) {
          if (page === 1) {
            return 0
          } else if (page === 2) {
            return 23
          } else {
            return 23 + (page - 2) * 24
          }
        } else {
          return 24 * (page - 1)
        }
      }

      const { response, error } = await useApi<CardsResponse>(
        type === CardType.ProductCard ? 'card.list' : 'card.templates',
        {
          limit: calculateLimit(),
          offset: calculateOffset(),
          ...spliceEmptyParams(this.filters),
        },
      )
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else {
        this.list[type].cards = this.list[type].cards.concat(response.cards)
        this.list[type].totalCount = response.total_count
        this.list[type].page++
      }
      this.isLoading[type] = false
    },
    async loadData(id: number): Promise<EditorData> {
      interface CardResponse {
        card: {
          id: number
          type: CardType
          name: string
          data: string
        }
      }

      const { response, error } = await useApi<CardResponse>('card.view', { id })
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response) {
        return JSON.parse(response.card.data) as EditorData
      }

      return Promise.reject(error ?? 'No response')
    },
    async cloneCard(id: number): Promise<number> {
      interface SavingResponse {
        id: number
      }
      const { response, error } = await useApi<SavingResponse>('card.clone', { id }, HttpMethod.POST)
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response) {
        return response.id
      }
      return Promise.reject(error ?? 'No response')
    },
  },
})
